import type { IconsMatchLevel } from './schema';

interface PlayTile {
  id: string;
  imageId: string;
  goalId: string;
}

interface PlayGoal {
  id: string;
  title: string;
  size: number;
  done: boolean;
  collected: number; // tiles sent directly to this goal
}

export class PlayView {
  private container: HTMLElement;
  private onWin: () => void;
  private onLose: () => void;
  private imageMap: Map<string, string> = new Map();

  private level: IconsMatchLevel | null = null;
  private goals: PlayGoal[] = [];           // sorted by order
  private goalNumbers = new Map<string, number>();
  private columns: PlayTile[][] = [];
  private tray: (PlayTile | null)[] = [];
  private traySize = 5;
  private activeGoalIdx = 0;
  private status: 'playing' | 'won' | 'lost' = 'playing';

  constructor(container: HTMLElement, onWin: () => void, onLose: () => void) {
    this.container = container;
    this.onWin = onWin;
    this.onLose = onLose;
  }

  // ── Public API ────────────────────────────────────────────────

  loadLevel(level: IconsMatchLevel): void {
    this.level = level;
    this.initState();
    this.render();
  }

  reset(): void {
    if (this.level) this.initState();
    this.render();
  }

  setActive(active: boolean): void {
    this.container.style.display = active ? '' : 'none';
  }

  setImageMap(map: Map<string, string>): void {
    this.imageMap = map;
  }

  // ── State init ────────────────────────────────────────────────

  private initState(): void {
    const l = this.level!;
    this.status = 'playing';
    this.traySize = l.traySize;
    this.activeGoalIdx = 0;

    // Sort goals by order
    const sortedGoals = [...l.goals].sort((a, b) => {
      const ai = l.goals.indexOf(a);
      const bi = l.goals.indexOf(b);
      return ai - bi; // level format stores them in order already
    });

    this.goalNumbers.clear();
    sortedGoals.forEach((g, i) => this.goalNumbers.set(g.id, i + 1));

    this.goals = sortedGoals.map(g => ({
      id: g.id,
      title: g.title,
      size: g.size,
      done: false,
      collected: 0,
    }));

    const tileData = new Map<string, { imageId: string; goalId: string }>();
    l.goals.forEach(g => g.tiles.forEach(t => tileData.set(t.id, { imageId: t.imageId, goalId: g.id })));

    this.columns = l.columns.map(col =>
      col.tiles.map(id => {
        const d = tileData.get(id)!;
        return { id, imageId: d.imageId, goalId: d.goalId };
      })
    );

    this.tray = Array<PlayTile | null>(this.traySize).fill(null);
  }

  // ── Actions ───────────────────────────────────────────────────

  private tapColumn(colIdx: number): void {
    if (this.status !== 'playing') return;
    const col = this.columns[colIdx];
    if (col.length === 0) return;

    const tile = col[0];
    const activeGoal = this.goals[this.activeGoalIdx];

    // Active goal tile → goes directly to the goal, bypasses tray
    if (activeGoal && tile.goalId === activeGoal.id) {
      col.shift();
      activeGoal.collected++;
      this.render();
      setTimeout(() => this.checkGoalComplete(), 80);
      return;
    }

    // Non-active tile → goes to tray
    const emptySlot = this.tray.indexOf(null);
    if (emptySlot === -1) return; // tray full, can't accept

    col.shift();
    this.tray[emptySlot] = tile;
    this.render();
    setTimeout(() => this.checkLose(), 80);
  }

  private checkGoalComplete(): void {
    if (this.status !== 'playing') return;
    const activeGoal = this.goals[this.activeGoalIdx];
    if (!activeGoal) return;

    if (activeGoal.collected < activeGoal.size) return;

    activeGoal.done = true;
    this.activeGoalIdx++;

    if (this.activeGoalIdx >= this.goals.length) {
      this.status = 'won';
      this.render();
      this.onWin();
      return;
    }

    this.render();
    // Cascade: new active goal may have tiles already in the tray
    setTimeout(() => this.checkCascade(), 60);
  }

  // Clear any tray tiles that belong to the newly-active goal.
  private checkCascade(): void {
    if (this.status !== 'playing') return;
    const activeGoal = this.goals[this.activeGoalIdx];
    if (!activeGoal) return;

    const cascadeSlots: number[] = [];
    for (let i = 0; i < this.tray.length; i++) {
      if (this.tray[i]?.goalId === activeGoal.id) cascadeSlots.push(i);
    }
    if (cascadeSlots.length === 0) {
      this.checkLose();
      return;
    }

    this.animateClear(cascadeSlots, () => {
      for (const i of cascadeSlots) this.tray[i] = null;
      activeGoal.collected += cascadeSlots.length;
      this.render();
      setTimeout(() => this.checkGoalComplete(), 60);
    });
  }

  private checkLose(): void {
    if (this.tray.includes(null)) return;
    this.status = 'lost';
    this.render();
    this.onLose();
  }

  private animateClear(slots: number[], onDone: () => void): void {
    const slotEls = this.container.querySelectorAll<HTMLElement>('.pv-tray-slot');
    for (const i of slots) {
      if (slotEls[i]) slotEls[i].classList.add('clearing');
    }
    setTimeout(onDone, 300);
  }

  // ── Render ────────────────────────────────────────────────────

  private render(): void {
    if (!this.level) {
      this.container.innerHTML =
        '<p class="placeholder">No level loaded. Open a level in the Editor tab first.</p>';
      return;
    }

    this.container.innerHTML = '';
    const view = document.createElement('div');
    view.className = 'play-view';

    view.appendChild(this.buildProgressBar());
    view.appendChild(this.buildGoalsPanel());
    view.appendChild(this.buildTrayPanel());
    view.appendChild(this.buildColumnsPanel());

    if (this.status === 'won') {
      view.appendChild(this.buildOverlay('win', 'Victory!', 'All goals completed.'));
    } else if (this.status === 'lost') {
      view.appendChild(this.buildOverlay('lose', 'Blocked!', 'Tray full — no match possible.'));
    }

    this.container.appendChild(view);
  }

  private buildProgressBar(): HTMLElement {
    const done = this.goals.filter(g => g.done).length;
    const total = this.goals.length;
    const pct = total === 0 ? 0 : Math.round((done / total) * 100);

    const wrap = document.createElement('div');
    wrap.className = 'pv-progress-wrap';

    const label = document.createElement('span');
    label.className = 'pv-progress-label';
    label.textContent = `${done} / ${total}`;

    const bar = document.createElement('div');
    bar.className = 'pv-progress-bar';

    const fill = document.createElement('div');
    fill.className = 'pv-progress-fill';
    fill.style.width = `${pct}%`;

    bar.appendChild(fill);
    wrap.append(label, bar);
    return wrap;
  }

  private buildGoalsPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'pv-goals';

    const activeGoal = this.goals[this.activeGoalIdx];
    const nextGoal   = this.goals[this.activeGoalIdx + 1];

    if (!activeGoal) return panel;

    panel.appendChild(this.buildGoalCard(activeGoal, 'active'));
    if (nextGoal) panel.appendChild(this.buildGoalCard(nextGoal, 'next'));

    return panel;
  }

  private buildGoalCard(goal: PlayGoal, role: 'active' | 'next'): HTMLElement {
    const n = this.goalNumbers.get(goal.id) ?? 0;
    // Next goal is a static preview — show only direct progress for active goal.
    const filled = role === 'active' ? goal.collected : 0;

    const card = document.createElement('div');
    card.className = `pv-goal-card ${role}`;

    const num = document.createElement('span');
    num.className = 'pv-goal-num';
    num.textContent = String(n);

    const title = document.createElement('span');
    title.className = 'pv-goal-title-text';
    title.textContent = goal.title || goal.id;

    const slots = document.createElement('div');
    slots.className = 'pv-goal-slots';
    for (let i = 0; i < goal.size; i++) {
      const dot = document.createElement('span');
      dot.className = 'pv-goal-dot' + (i < filled ? ' filled' : '');
      slots.appendChild(dot);
    }

    card.append(num, title, slots);
    return card;
  }

  private buildColumnsPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'pv-columns';

    const trayFull = !this.tray.includes(null);

    this.columns.forEach((col, idx) => {
      const isEmpty = col.length === 0;
      const canTap  = !isEmpty && this.status === 'playing' && !trayFull;

      const colEl = document.createElement('div');
      colEl.className = 'pv-column' +
        (isEmpty ? ' empty'   : '') +
        (canTap  ? ' tappable': '');

      if (canTap) colEl.addEventListener('click', () => this.tapColumn(idx));

      if (!isEmpty) {
        const top = col[0];
        const goalN = this.goalNumbers.get(top.goalId) ?? '?';
        const topEl = document.createElement('div');
        topEl.className = 'pv-col-top-tile';
        topEl.title = top.imageId || top.id;
        topEl.appendChild(this.tileContent(top.imageId, String(goalN)));
        colEl.appendChild(topEl);
      }

      const stackEl = document.createElement('div');
      stackEl.className = 'pv-col-stack';

      const rest = isEmpty ? 0 : col.length - 1;
      const maxVisible = 3;
      for (let i = 0; i < Math.min(rest, maxVisible); i++) {
        const t = col[i + 1];
        const goalN = this.goalNumbers.get(t.goalId) ?? '?';
        const dot = document.createElement('div');
        dot.className = 'pv-stack-dot';
        dot.appendChild(this.tileContent(t.imageId, String(goalN)));
        stackEl.appendChild(dot);
      }
      if (rest > maxVisible) {
        const more = document.createElement('div');
        more.className = 'pv-stack-more';
        more.textContent = `+${rest - maxVisible}`;
        stackEl.appendChild(more);
      }
      if (isEmpty) {
        const em = document.createElement('div');
        em.className = 'pv-col-empty-label';
        em.textContent = '—';
        stackEl.appendChild(em);
      }

      colEl.appendChild(stackEl);
      panel.appendChild(colEl);
    });

    return panel;
  }

  private buildTrayPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'pv-tray';

    const label = document.createElement('div');
    label.className = 'pv-tray-label';
    label.textContent = 'TRAY';
    panel.appendChild(label);

    const slots = document.createElement('div');
    slots.className = 'pv-tray-slots';

    for (let i = 0; i < this.traySize; i++) {
      const tile = this.tray[i];
      const slot = document.createElement('div');
      if (tile) {
        const goalN = this.goalNumbers.get(tile.goalId) ?? '?';
        slot.className = 'pv-tray-slot occupied';
        slot.title = tile.imageId || tile.id;
        slot.appendChild(this.tileContent(tile.imageId, String(goalN)));
      } else {
        slot.className = 'pv-tray-slot';
      }
      slots.appendChild(slot);
    }

    panel.appendChild(slots);
    return panel;
  }

  private tileContent(imageId: string, fallback: string): HTMLElement {
    const url = imageId ? this.imageMap.get(imageId) : undefined;
    if (url) {
      const img = document.createElement('img');
      img.src = url;
      img.className = 'ie-tile-img';
      img.alt = imageId;
      return img;
    }
    const span = document.createElement('span');
    span.textContent = fallback;
    return span;
  }

  private buildOverlay(type: 'win' | 'lose', heading: string, sub: string): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = `pv-overlay ${type}`;
    const h = document.createElement('div');
    h.className = 'pv-overlay-heading';
    h.textContent = heading;
    const s = document.createElement('div');
    s.className = 'pv-overlay-sub';
    s.textContent = sub;
    const btn = document.createElement('button');
    btn.className = 'pv-overlay-btn';
    btn.textContent = 'Play Again';
    btn.addEventListener('click', () => this.reset());
    overlay.append(h, s, btn);
    return overlay;
  }
}
